import { useCallback } from 'react';
import { toast } from '@chargebee/sting-react';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useCreatePollingRequest } from '@bbkAdminRedux/rtkq/polling.slice';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

export const apiErrorMessage = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : undefined;
};

/** Poll statuses have no API message; POST rejections use the backend body. */
export const translationExportErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    switch (error.message) {
      case 'failed':
        return 'Export failed, please try again.';
      case 'expired':
        return 'Export link expired, please try again.';
      case 'timeout':
      case 'unknown status':
        return 'It took too long to generate file, please try again.';
      default:
        break;
    }
  }

  return (
    apiErrorMessage(error) ??
    'An error occurred during export, please try again.'
  );
};

/** Platform-level locale exposed by `GET /api/v2/languages/available`. */
export type AvailableLocale = {
  locale_code: string;
  display_name: string;
  rtl: boolean;
  enabled: boolean;
  sort_order: number;
};

/** Exportable resources (`BRAND_LABELS` not wired yet). */
export type TranslatableResource =
  | 'OFFER'
  | 'LOSS_AVERSION_CARD'
  | 'CONFIRMATION_PAGE'
  | 'CANCEL_PAGE'
  | 'VANITY_REASON';

export type TranslationExportSelection = {
  resource: TranslatableResource;
  resource_id: string;
};

export type TranslationExportOptions = {
  content_scope: 'ALL' | 'SELECTED';
  csv_format?: 'PLAIN' | 'BOM';
  /** Omit for all enabled non-base locales. */
  locale?: string | null;
  selections?: TranslationExportSelection[];
};

export type ExportTranslationsArgs = {
  appKey: string;
  translation_export: TranslationExportOptions;
};

export type TranslationImportStatus =
  'RUNNING' | 'COMPLETED' | 'COMPLETED_WITH_ERRORS' | 'FAILED';

/** FAILED import reason from the backend (`code` + `message`). */
export type TranslationImportErrorCode =
  | 'coordinator_launch_failed'
  | 'coordinator_failed'
  | 'upload_unreadable'
  | 'deadline_reached'
  | 'nothing_applied'
  | 'resource_publish_failed';

export type TranslationImportError = {
  code: TranslationImportErrorCode;
  message: string;
};

export type TranslationImport = {
  id: string;
  application_id: string;
  status: TranslationImportStatus;
  total_resources?: number;
  completed_resources: number;
  failed_resources: number;
  /** Present when status is FAILED. */
  error?: TranslationImportError;
  report_available?: boolean;
  created_at: string;
  updated_at: string;
};

export type SubmitTranslationImportArgs = AppKeyArg & {
  file: File;
};

export type TranslationImportReport = {
  url: string;
  expires_in_seconds: number;
};

/**
 * Locale enabled on an application, returned by `GET /api/v2/apps/{appKey}/languages`.
 *
 * `rule_id` is the hashids-encoded id of the attached `LOCALE_MATCHING` rule (matching the
 * encoding used by other API IDs e.g. `Rule.application_id`). `null` / `undefined` mean the
 * locale currently has no matching rule attached.
 *
 * `client_formatted_rule` is the Brightback expression for the attached rule, included by the
 * languages list endpoint so the matching-rule cell can render on page load without a follow-up
 * fetch. Null / undefined when the locale has no rule (or its rule was deleted / inactivated).
 */
export type ApplicationLocale = {
  locale_code: string;
  base: boolean;
  enabled: boolean;
  rule_id?: string | null;
  client_formatted_rule?: string | null;
};

export type LanguageSourceType = 'API_JS' | 'BILLING';

export type LanguageSourceConfig = {
  source_type: LanguageSourceType;
  field_key: string;
  enabled: boolean;
};

type AppKeyArg = { appKey: string };
type LocaleCodeArg = AppKeyArg & { localeCode: string };

/**
 * Args for the consolidated `PUT /api/v2/apps/{app}/languages/{localeCode}` endpoint.
 * Every field is optional; at least one must be present.
 *
 * - `enabled: false` deactivates the locale.
 * - `enabled: true` (or omitted alongside other fields) ensures the locale row exists & is enabled.
 * - `base: true` promotes the locale to the base / fallback locale.
 * - `clientFormattedRule` (non-empty, only honored when `enabled !== false`) creates or updates
 *   the `LOCALE_MATCHING` rule attached to the locale.
 */
export type UpsertApplicationLocaleArgs = LocaleCodeArg & {
  base?: boolean;
  enabled?: boolean;
  clientFormattedRule?: string;
};

export const languagesSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAvailableLocales: build.query<AvailableLocale[], void>({
      query: () => `/api/v2/languages/available`,
    }),
    getApplicationLocales: build.query<ApplicationLocale[], AppKeyArg>({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/languages`,
      providesTags: ['ApplicationLocales'],
    }),
    upsertApplicationLocale: build.mutation<
      ApplicationLocale,
      UpsertApplicationLocaleArgs
    >({
      query: ({ appKey, localeCode, base, enabled, clientFormattedRule }) => ({
        url: `/api/v2/apps/${appKey}/languages/${localeCode}`,
        method: 'PUT',
        body: {
          base,
          enabled,
          client_formatted_rule: clientFormattedRule,
        },
      }),
      invalidatesTags: ['ApplicationLocales'],
    }),
    getLanguageSourceConfig: build.query<
      LanguageSourceConfig | null,
      AppKeyArg
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/language-source`,
      providesTags: ['LanguageSourceConfig'],
    }),
    configureLanguageSource: build.mutation<
      LanguageSourceConfig,
      AppKeyArg & {
        sourceType: LanguageSourceType;
        fieldKey: string;
        enabled: boolean;
      }
    >({
      query: ({ appKey, sourceType, fieldKey, enabled }) => ({
        url: `/api/v2/apps/${appKey}/language-source`,
        method: 'PUT',
        body: {
          source_type: sourceType,
          field_key: fieldKey,
          enabled,
        },
      }),
      invalidatesTags: ['LanguageSourceConfig'],
    }),
    /** Starts a translation CSV export; response includes poll `statusURL`. */
    exportTranslations: build.query<
      { statusURL: string },
      ExportTranslationsArgs
    >({
      query: ({ appKey, translation_export }) => ({
        url: `/api/v1/apps/${appKey}/translations/export`,
        method: 'POST',
        body: { translation_export },
      }),
    }),
    submitTranslationImport: build.mutation<
      TranslationImport,
      SubmitTranslationImportArgs
    >({
      query: ({ appKey, file }) => {
        const body = new FormData();
        body.append('file', file);
        return {
          url: `/api/v2/apps/${appKey}/translations/imports`,
          method: 'POST',
          body,
        };
      },
    }),
    getLatestTranslationImport: build.query<
      TranslationImport | null,
      AppKeyArg
    >({
      query: ({ appKey }) =>
        `/api/v2/apps/${appKey}/translations/imports/latest`,
    }),
    getTranslationImportReport: build.query<
      TranslationImportReport,
      AppKeyArg & { importId: string }
    >({
      query: ({ appKey, importId }) =>
        `/api/v2/apps/${appKey}/translations/imports/${importId}/report`,
    }),
  }),
});

export const {
  useGetAvailableLocalesQuery,
  useGetApplicationLocalesQuery,
  useUpsertApplicationLocaleMutation,
  useGetLanguageSourceConfigQuery,
  useConfigureLanguageSourceMutation,
  useSubmitTranslationImportMutation,
  useGetLatestTranslationImportQuery,
  useLazyGetTranslationImportReportQuery,
} = languagesSlice;

/** POST export → poll → download CSV. */
export const useExportTranslations = () => {
  const [exportTranslations$] =
    languagesSlice.endpoints.exportTranslations.useLazyQuery();
  const pollExport = useCreatePollingRequest(exportTranslations$);

  return useCallback(
    async (args: ExportTranslationsArgs) => {
      toast({
        description:
          'Export being generated. Please wait. A copy will also be emailed when ready.',
        variant: 'info',
        duration: 8000,
      });

      try {
        const downloadUrl = await pollExport(args);
        toast({
          description: 'Export successfully generated.',
          variant: 'success',
        });
        window.location.href = downloadUrl;
      } catch (err: unknown) {
        GlobalLogger.error(err);
        toast({
          description: translationExportErrorMessage(err),
          variant: 'danger',
          duration: 6000,
        });
        throw err;
      }
    },
    [pollExport]
  );
};
